/**
 * Patient / episode / consent contract (A2/A3, increment-6 plan §5).
 *
 * The episode is the spine: it pins the protocol version and the consent-text
 * version at enrollment (what the patient was enrolled under is a historical
 * fact), carries the operated side the overlay and ROM targets consume
 * (A2-side), and owns the consent facts every session under it derives from.
 *
 * TM-07 closes here: `createSessionRequestSchema` no longer carries a
 * client-asserted consent flag — the server derives the session's immutable
 * consent fact from the episode's consent HEAD (latest non-superseded row).
 */

import { z } from 'zod';
import { flagCodeWireSchema } from './flags';
import { protocolVersionPinSchema } from './protocolSelection';

/**
 * Consent-text versions the platform can capture against. Append-only: a row
 * captured under a version must keep resolving to it forever.
 *
 * `AN-TKR-consent-DRAFT@0.1` is the OQ10 engineering draft — kept so the rows
 * captured under it stay valid; no build offers it any more.
 *
 * `AN-TKR-consent@1.0` is the accepted patient consent
 * (docs/consent/AN-TKR-patient-consent-v1.0.md — owner, 2026-09-08; ADR-0029),
 * the version every new real enrolment pins (`CURRENT_PATIENT_CONSENT_TEXT_VERSION`).
 *
 * `AN-PRACTICE-CONSENT@0.1` (ADR-0018 §5) is a different document for a
 * different person — a real volunteer practising with a clinician, never a
 * patient. `services/episodes.ts` derives the retention terms an enrollment
 * gets FROM which of these the caller sends; adding a version here without
 * also extending that lookup is a compile error by construction
 * (`Record<ConsentTextVersion, RetentionPin>`), not a silent gap.
 */
export const CONSENT_TEXT_VERSIONS = [
  'AN-TKR-consent-DRAFT@0.1',
  'AN-PRACTICE-CONSENT@0.1',
  'AN-TKR-consent@1.0',
] as const;
export type ConsentTextVersion = (typeof CONSENT_TEXT_VERSIONS)[number];

/** The patient consent text every new real enrolment pins (ADR-0029 §3). */
export const CURRENT_PATIENT_CONSENT_TEXT_VERSION: ConsentTextVersion = 'AN-TKR-consent@1.0';

export const OPERATED_SIDES = ['left', 'right'] as const;
export type OperatedSide = (typeof OPERATED_SIDES)[number];

export const PATIENT_SEXES = ['female', 'male', 'other'] as const;
export type PatientSex = (typeof PATIENT_SEXES)[number];

/** Calendar date (no time-of-day): surgery and rehab-start are day facts. */
const isoDateSchema = z.iso.date();

// ---- enrollment (A2 wizard finish — one atomic write) ----------------------

export const enrollPatientSchema = z.object({
  name: z.string().trim().min(1).max(200),
  ageYears: z.number().int().min(0).max(130).optional(),
  sex: z.enum(PATIENT_SEXES).optional(),
  heightCm: z.number().positive().max(300).optional(),
  weightKg: z.number().positive().max(500).optional(),
  diagnosis: z.string().trim().min(1).max(500).optional(),
  comorbidities: z.array(z.string().trim().min(1).max(200)).max(20).default([]),
  supportPerson: z.string().trim().min(1).max(200).optional(),
});

export const enrollEpisodeSchema = z.object({
  procedure: z.string().trim().min(1).max(300).optional(),
  operatedSide: z.enum(OPERATED_SIDES),
  surgeryDate: isoDateSchema,
  rehabStartDate: isoDateSchema,
  surgeon: z.string().trim().min(1).max(200).optional(),
  facility: z.string().trim().min(1).max(200).optional(),
  /** The pin the episode enrolls under; the server validates it resolves. */
  protocolVersion: protocolVersionPinSchema,
});

export const enrollConsentSchema = z.object({
  /** Which rendered text the patient was taken through (must be known). */
  consentTextVersion: z.enum(CONSENT_TEXT_VERSIONS),
  /** Draft item (b): sessions may be video-recorded. Explicit, never defaulted. */
  recordingConsent: z.boolean(),
  /** Draft item (c) / ADR-0004: full retention of video + interpretation data. */
  fullRetentionAck: z.boolean(),
  /** Device time of the capture moment (the wizard's consent step). */
  occurredAt: z.iso.datetime(),
});

/** POST /api/patients — the wizard's finish. Mints patient + episode + consent atomically. */
export const enrollmentRequestSchema = z.object({
  patient: enrollPatientSchema,
  episode: enrollEpisodeSchema,
  consent: enrollConsentSchema,
});
export type EnrollmentRequest = z.infer<typeof enrollmentRequestSchema>;

// ---- read shapes -----------------------------------------------------------

export const episodeConsentSchema = z.object({
  id: z.uuid(),
  episodeId: z.uuid(),
  consentTextVersion: z.string().min(1),
  recordingConsent: z.boolean(),
  fullRetentionAck: z.boolean(),
  occurredAt: z.iso.datetime(),
  recordedAt: z.iso.datetime(),
  recordedBy: z.string().min(1),
});
export type EpisodeConsent = z.infer<typeof episodeConsentSchema>;

export const episodeSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  createdAt: z.iso.datetime(),
  createdBy: z.string().min(1),
  protocolVersion: z.string().min(1),
  consentTextVersion: z.string().min(1),
  procedure: z.string().nullable(),
  operatedSide: z.enum(OPERATED_SIDES),
  surgeryDate: isoDateSchema,
  rehabStartDate: isoDateSchema.nullable(),
  surgeon: z.string().nullable(),
  facility: z.string().nullable(),
  /**
   * The consent HEAD's derived capture verdict (C8): recording consent AND the
   * full-retention acknowledgment, both — recording without the retention
   * acknowledgment has no supported storage story (ADR-0004), so it is not a
   * consented recording. Servers derive it; clients only render it.
   */
  recordingConsented: z.boolean(),
});
export type Episode = z.infer<typeof episodeSchema>;

export const patientSchema = z.object({
  id: z.uuid(),
  createdAt: z.iso.datetime(),
  createdBy: z.string().min(1),
  name: z.string().min(1),
  ageYears: z.number().int().nullable(),
  sex: z.enum(PATIENT_SEXES).nullable(),
  heightCm: z.number().nullable(),
  weightKg: z.number().nullable(),
  diagnosis: z.string().nullable(),
  comorbidities: z.array(z.string()),
  supportPerson: z.string().nullable(),
});
export type Patient = z.infer<typeof patientSchema>;

export const enrollmentResponseSchema = z.object({
  ok: z.literal(true),
  patient: patientSchema,
  episode: episodeSchema,
  consent: episodeConsentSchema,
});
export type EnrollmentResponse = z.infer<typeof enrollmentResponseSchema>;

// ---- the patient-list summary (G1, increment-8 plan §1 PR4) ----------------

/**
 * Where the program stands today (IST), for the card's day pill and the
 * flags-first sort's third key. `state` tells the truth about what `day` is:
 * `today` = a program day falls today; `upcoming` = no slot today, `day` is
 * the NEXT program day (covers rest days mid-program and pre-start alike);
 * `complete` = every slot's day has passed, `day` is the last slot's.
 */
export const programDayPositionSchema = z.object({
  state: z.enum(['today', 'upcoming', 'complete']),
  day: z.number().int().min(1),
  week: z.number().int().min(1),
  dayOfWeek: z.number().int().min(1),
});
export type ProgramDayPosition = z.infer<typeof programDayPositionSchema>;

/**
 * The G1 card's derived facts, computed server-side per entry so the list and
 * its sort can never disagree (the sort runs over these same numbers):
 *
 *  - open flags = raise rows with no resolution row on the latest episode, in
 *    raise order (the lifecycle's own OPEN definition — no status enum);
 *  - the WOMAC head/previous pair = the two latest COMPLETE-WOMAC assessment
 *    heads ("vs previous", G1's own wording — the adopted report only ever
 *    compares baseline→discharge, which cannot satisfy it);
 *  - `assessmentDueToday` = today's slot is an assessment day AND that
 *    timepoint has neither a filed record nor a recorded miss — "due" stays
 *    truthful after the work is done.
 */
export const patientListSummarySchema = z.object({
  openFlags: z.object({
    count: z.number().int().min(0),
    codes: z.array(flagCodeWireSchema),
  }),
  womacHead: z
    .object({
      timepointDay: z.number().int().min(1),
      total: z.number().int().min(0),
      /** Band label from instrument data (protocol WOMAC_BANDS) — never recomputed client-side. */
      bandLabel: z.string().min(1),
    })
    .nullable(),
  womacPrevious: z
    .object({
      timepointDay: z.number().int().min(1),
      total: z.number().int().min(0),
    })
    .nullable(),
  /** Null when the episode has no dated calendar (no episode, or a degenerate fixture). */
  programDay: programDayPositionSchema.nullable(),
  sessionToday: z.boolean(),
  assessmentDueToday: z.boolean(),
});
export type PatientListSummary = z.infer<typeof patientListSummarySchema>;

/**
 * GET /api/patients — one G1 card's worth per entry. Server-sorted
 * flags-first (any-flag → more-flags → earlier-day), so clients render in
 * order received and never re-sort.
 */
export const patientListEntrySchema = z.object({
  patient: patientSchema,
  /** The patient's latest episode; null never actually occurs via enrollment. */
  episode: episodeSchema.nullable(),
  summary: patientListSummarySchema,
});
export type PatientListEntry = z.infer<typeof patientListEntrySchema>;
export const listPatientsResponseSchema = z.object({
  ok: z.literal(true),
  patients: z.array(patientListEntrySchema),
});
export type ListPatientsResponse = z.infer<typeof listPatientsResponseSchema>;

/** GET /api/patients/:patientId */
export const patientDetailResponseSchema = z.object({
  ok: z.literal(true),
  patient: patientSchema,
  episode: episodeSchema.nullable(),
  consent: episodeConsentSchema.nullable(),
});
export type PatientDetailResponse = z.infer<typeof patientDetailResponseSchema>;

// ---- the dated calendar (A4/B2, PR5) ---------------------------------------

/**
 * One dated slot as the calendar serves it: the program position, its date,
 * what the protocol says about it (phase, hold dosing, assessment day), the
 * session bound to it if any, and the DERIVED missed verdict — a dated slot
 * with no session whose day has passed. Missed is computed at read time,
 * never stored: there is no column to falsify (B2 by construction).
 */
export const calendarEntrySchema = z.object({
  slotId: z.uuid(),
  slotDay: z.number().int().min(1),
  week: z.number().int().min(1),
  dayOfWeek: z.number().int().min(1),
  calendarDate: z.iso.date(),
  phaseNumber: z.number().int().min(1),
  phaseName: z.string().min(1),
  isAssessment: z.boolean(),
  /** The week's hold dosing (min === max renders as one number). Null when
   *  the pinned template no longer resolves — surfaced, never invented. */
  holdSecondsMin: z.number().nullable(),
  holdSecondsMax: z.number().nullable(),
  session: z
    .object({
      id: z.uuid(),
      status: z.string().min(1),
      endDisposition: z.string().nullable(),
    })
    .nullable(),
  missed: z.boolean(),
});
export type CalendarEntry = z.infer<typeof calendarEntrySchema>;

/** GET /api/episodes/:episodeId/calendar */
export const episodeCalendarResponseSchema = z.object({
  ok: z.literal(true),
  episodeId: z.uuid(),
  protocolVersion: z.string().min(1),
  /** The server's "today" (IST) the missed verdicts were derived against. */
  derivedAgainstDate: z.iso.date(),
  entries: z.array(calendarEntrySchema),
});
export type EpisodeCalendarResponse = z.infer<typeof episodeCalendarResponseSchema>;
